import { useState, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import type { RegisterFlow } from '../hooks/useRegisterFlow'
import styles from './OtpStep.module.css'

type OtpStepProps = {
  flow: RegisterFlow
}

const RESEND_SECONDS = 45

export function OtpStep({ flow }: OtpStepProps) {
  const [value, setValue] = useState('')
  const [hasShake, setHasShake] = useState(false)
  const [countdown, setCountdown] = useState(RESEND_SECONDS)
  const [canResend, setCanResend] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevError = useRef<string | null>(null)

  // Start countdown on mount (step remounts on each entry due to key prop)
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  // Trigger shake animation when a new error arrives
  useEffect(() => {
    if (flow.error && flow.error !== prevError.current) {
      prevError.current = flow.error
      setHasShake(true)
      setValue('')
      const id = setTimeout(() => setHasShake(false), 350)
      return () => clearTimeout(id)
    }
    return undefined
  }, [flow.error])

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8)
    setValue(digits)
    prevError.current = null // clear tracked error so next error triggers shake

    if (digits.length === 8) {
      setTimeout(() => {
        void flow.verifyOtp(digits)
      }, 400)
    }
  }

  function handleResend() {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setCountdown(RESEND_SECONDS)
    setCanResend(false)
    setValue('')
    void flow.resendOtp()

    // Restart countdown
    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const minutes = Math.floor(countdown / 60)
  const seconds = countdown % 60
  const countdownLabel = `${minutes}:${String(seconds).padStart(2, '0')}`

  return (
    <div className={styles.step}>
      <h1 className={styles.heading}>Confirme o código</h1>

      <div className={styles.otpContext}>
        <p className={styles.otpContextLine}>Código enviado para</p>
        <p className={styles.otpContextEmail}>
          <span>{flow.email}</span>
          <button
            type="button"
            className={styles.changeEmailBtn}
            onClick={flow.goBack}
          >
            Alterar
          </button>
        </p>
      </div>

      <div className={styles.otpWrap}>
        <input
          className={clsx(
            styles.otpInput,
            value.length === 8 && styles.otpInputFilled,
            hasShake && styles.otpInputError,
          )}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          placeholder="————————"
          value={value}
          onChange={handleInput}
          autoFocus
          disabled={flow.isLoading}
          aria-label="Código de verificação de 8 dígitos"
          aria-invalid={hasShake}
          aria-describedby="otp-error-msg"
        />
      </div>

      <p
        id="otp-error-msg"
        className={styles.otpError}
        role="alert"
        aria-live="polite"
      >
        {flow.error ?? '\u00A0'}
      </p>

      <p className={styles.otpNote}>
        Válido por <strong>15 minutos</strong> · Verifique a pasta de spam
      </p>

      <div className={styles.resendRow}>
        <span className={styles.resendText}>
          Não recebeu?{' '}
          <button
            type="button"
            className={styles.resendBtn}
            disabled={!canResend || flow.isLoading}
            onClick={handleResend}
          >
            {canResend ? (
              'Reenviar código'
            ) : (
              <>
                Reenviar em{' '}
                <span className={styles.countdown}>{countdownLabel}</span>
              </>
            )}
          </button>
        </span>
      </div>
    </div>
  )
}
